import { useEffect, useMemo, useState } from "react"
import { ACTIONS, EVENTS, STATUS, type CallBackProps, type Step } from "react-joyride"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalCache, StorageKey } from "../../../../../../shared/cache/LocalCache"
import { StandardTour } from "../../../../../../shared/components/tour/StandardTour"
import { ProductTourTooltip } from "../../../../../../shared/components/tour/ProductTourTooltip"
import { waitForTourTarget, clickTourTarget } from "../../../../../../shared/components/tour/tourUtils"

function expandProductionMenu() {
    clickTourTarget("[data-tour='merchant-product-tour-nav-production']")
}

export function TakeawayAnnouncementTour() {
    const { l10n } = useAppLocalization()
    const localCache = LocalCache()

    const [run, setRun] = useState(false)
    const [stepIndex, setStepIndex] = useState(0)

    useEffect(() => {
        const seen = localCache.get(StorageKey.TAKEAWAY_ANNOUNCEMENT_TOUR_SEEN)
        if (seen === "1") return

        setStepIndex(0)
        setRun(true)
    }, [])

    const steps = useMemo<Step[]>(
        () => [
            {
                target: "body",
                placement: "center",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="takeaway-announcement-tour-step-intro">
                        <>We have launched a dedicated page for takeaway.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='takeaway-announcement-tour-nav-takeaway']",
                placement: "right",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="takeaway-announcement-tour-step-nav-takeaway">
                        <>From now on you will find takeaway orders here.</>
                    </LocalizedStrict>
                ),
            },
        ],
        [],
    )

    const locale = useMemo(
        () => ({
            back: l10n.getString("merchant-product-tour-locale-back"),
            close: l10n.getString("merchant-product-tour-locale-close"),
            last: l10n.getString("merchant-product-tour-locale-last"),
            next: l10n.getString("merchant-product-tour-locale-next"),
            skip: l10n.getString("merchant-product-tour-locale-skip"),
        }),
        [l10n],
    )

    function persistSeen() {
        localCache.set(StorageKey.TAKEAWAY_ANNOUNCEMENT_TOUR_SEEN, "1")
    }

    function handleCallback(data: CallBackProps) {
        const { action, index, status, type } = data

        const isFinished = status === STATUS.FINISHED
        const isSkipped = status === STATUS.SKIPPED
        const isClosed = action === ACTIONS.CLOSE
        const isSkippedAction = action === ACTIONS.SKIP

        if (isFinished || isSkipped || isClosed || isSkippedAction) {
            persistSeen()
            setRun(false)
            return
        }

        const isStepAfter = type === EVENTS.STEP_AFTER
        const isTargetNotFound = type === EVENTS.TARGET_NOT_FOUND
        if (isStepAfter || isTargetNotFound) {
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)

            if (nextIndex === 1) {
                expandProductionMenu()
                void waitForTourTarget("[data-tour='takeaway-announcement-tour-nav-takeaway']", {
                    attempts: 30,
                    intervalMs: 50,
                }).then(() => {
                    setStepIndex(1)
                })
                return
            }

            setStepIndex(nextIndex)
        }
    }

    return (
        <StandardTour
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            callback={handleCallback}
            locale={locale}
            tooltipComponent={ProductTourTooltip}
        />
    )
}
